import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent, FocusEvent } from 'react';
import { formatDateLatam, isValidLatamDate, parseLatamDate } from '../lib/dates';

interface DateInputProps {
  id?: string;
  value: string;
  onChange: (latam: string) => void;
  required?: boolean;
  placeholder?: string;
  testId?: string;
}

/**
 * Input controlado en formato latino dd/MM/yyyy.
 * Acepta entrada inicial en ISO (yyyy-MM-dd) o latino y normaliza al renderizar.
 * Auto-formatea mientras el usuario escribe (ej. "9522026" -> "09/52/2026").
 */
export default function DateInput({ id, value, onChange, required, placeholder, testId }: DateInputProps) {
  const [text, setText] = useState<string>(() => formatDateLatam(value));
  const [touched, setTouched] = useState(false);
  const isFocused = useRef(false);

  useEffect(() => {
    if (!isFocused.current) {
      setText(formatDateLatam(value));
    }
  }, [value]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Allow only digits and slashes; auto-insert slashes after dd and mm.
    const digitsOnly = raw.replace(/[^\d]/g, '').slice(0, 8);
    let formatted = digitsOnly;
    if (digitsOnly.length > 4) {
      formatted = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2, 4)}/${digitsOnly.slice(4)}`;
    } else if (digitsOnly.length > 2) {
      formatted = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2)}`;
    }
    setText(formatted);
    onChange(formatted);
  };

  const handleBlur = (_e: FocusEvent<HTMLInputElement>) => {
    isFocused.current = false;
    setTouched(true);
  };

  const invalid = touched && text !== '' && !isValidLatamDate(text);

  return (
    <>
      <input
        id={id}
        type="text"
        inputMode="numeric"
        pattern="\d{2}/\d{2}/\d{4}"
        required={required}
        placeholder={placeholder || 'dd/mm/aaaa'}
        value={text}
        onChange={handleChange}
        onFocus={() => { isFocused.current = true; }}
        onBlur={handleBlur}
        maxLength={10}
        data-testid={testId}
      />
      {invalid && (
        <small className="muted" style={{ color: '#b91c1c' }}>
          Fecha inválida. Usa el formato dd/mm/aaaa.
        </small>
      )}
    </>
  );
}

// Re-export helpers so importing components don't need both modules.
export { formatDateLatam, parseLatamDate, isValidLatamDate };
